import { type GeoState } from '@vakantiesnl/services/src/hooks/queries';
import { type FilterState } from '@vakantiesnl/services/src/stores/filtersStore';
import { type MappedDestinationView } from '@vakantiesnl/types/src/geo';

/** Helper to only get the regions slugs which have a matched country selected */
export const filterValidRegionSlugs = (filterState: FilterState, geoToCheck: string[], geo: GeoState): string[] => {
	const selectedCountrySlugs = getSelectedCountriesFromSlugs(filterState.countrySlugs, geo.countries).map(
		(country) => country.slug,
	);

	const validRegionsSlugs = geoToCheck.filter((slug) =>
		Object.keys(geo.regions)
			.filter((countrySlug) => selectedCountrySlugs?.includes(countrySlug))
			.map((countrySlug) => geo.regions[countrySlug])
			.flat()
			.some((region) => region.slug === slug),
	);

	return validRegionsSlugs;
};

export const getSelectedCountriesFromSlugs = (
	countrySlugs: FilterState['countrySlugs'],
	countries: GeoState['countries'],
): MappedDestinationView[] => countries.filter((country: MappedDestinationView) => countrySlugs.includes(country.slug));

type SelectedGeo = {
	countries: MappedDestinationView[];
	regions: MappedDestinationView[];
	cities: MappedDestinationView[];
};

type SelectedGeoSlugs = {
	countrySlugs: string[];
	regionSlugs: string[];
	citySlugs: string[];
};

/**
 * Helper to get filtered geo from slugs
 * ONLY returning geo that have a parent geo that matches a slug
 * ONLY returning cities when max 1 country has been selected
 */
export const getSelectedGeoFromSlugs = (selectedGeoSlugs: SelectedGeoSlugs, geo: GeoState): SelectedGeo => {
	const selectedGeo: SelectedGeo = {
		countries: [],
		regions: [],
		cities: [],
	};

	const selectedCountries = getSelectedCountriesFromSlugs(selectedGeoSlugs.countrySlugs, geo.countries);
	if (selectedCountries?.length > 0) selectedGeo.countries = selectedCountries;

	const selectedRegions = getSelectedRegions(geo, selectedCountries, selectedGeoSlugs.regionSlugs);
	if (selectedRegions?.length > 0) selectedGeo.regions = selectedRegions;

	const selectedCities = getSelectedCities(geo, selectedRegions, selectedGeoSlugs.citySlugs);
	if (selectedCities?.length > 0 && selectedCountries.length < 2) selectedGeo.cities = selectedCities;

	return selectedGeo;
};

export const getSelectedRegions = (
	geo: GeoState,
	selectedCountries: MappedDestinationView[],
	selectedRegionSlugs: FilterState['regionSlugs'],
): MappedDestinationView[] => {
	return Object.keys(geo.regions)
		.filter((countrySlug) => selectedCountries.map((country) => country.slug).includes(countrySlug))
		.map((countrySlug) => geo.regions[countrySlug])
		.flat()
		.filter((region) => selectedRegionSlugs.includes(region.slug));
};

export const getSelectedCities = (
	geo: GeoState,
	selectedRegions: MappedDestinationView[],
	selectedCitySlugs: FilterState['citySlugs'],
): MappedDestinationView[] => {
	return Object.keys(geo.cities)
		.filter((regionSlug) => selectedRegions.map((region) => region.slug).includes(regionSlug))
		.map((regionSlug) => geo.cities[regionSlug])
		.flat()
		.filter((city) => selectedCitySlugs.includes(city.slug));
};
